/**
 * Elenco delle uscite di rifiuti prodotti (V71).
 *
 * DEFAULT «tutte tranne annullate»: il backend le restituisce tutte, perché
 * `annullata` è uno dei valori di `stato` e un filtro che non trova mai nulla
 * non serve. Qui si nascondono per default — un FIR annullato è rumore nel
 * lavoro di ogni giorno — con un interruttore che le rimette in elenco, e non
 * un filtro che va indovinato.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Truck, Plus } from 'lucide-react';
import {
  listUscite, setEcomaticoUscita,
  type UscitaFiltri, type UscitaListItem, type UscitaListResponse,
  type FiltroEcomatico, type StatoUscita, type TipoOperazione,
} from '../api/uscite';
import { getCerRifiutiProdotti, type CerRifiutoProdotto } from '../api/settings';
import { EcomaticoBadge } from '../components/magazzino/Ecomatico';
import ClienteSearch from '../components/uscite/ClienteSearch';
import StatoUscitaBadge from '../components/uscite/StatoUscitaBadge';
import { useAuthStore } from '../store/authStore';
import { fmtKg } from '../lib/saldo';
import { canUscite } from '../lib/constants';

const inputCls = 'text-sm text-ink-dark border border-stone-border rounded px-2 py-1.5 focus:outline-none focus:border-sage bg-white';
const LIMIT = 50;

function fmtData(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString('it-IT') : '—';
}

export default function UscitePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [res, setRes] = useState<UscitaListResponse | null>(null);
  const [rifiuti, setRifiuti] = useState<CerRifiutoProdotto[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [dataDa, setDataDa] = useState('');
  const [dataA, setDataA] = useState('');
  const [cliente, setCliente] = useState<{ id: string; ragione_sociale: string } | null>(null);
  const [rifiutoId, setRifiutoId] = useState('');
  const [direzione, setDirezione] = useState<'' | TipoOperazione>('');
  const [stato, setStato] = useState<'' | StatoUscita>('');
  const [mostraAnnullate, setMostraAnnullate] = useState(false);
  // Deep-link dalla coda di lavoro della Dashboard: il filtro arriva dall'URL e
  // resta VISIBILE nel controllo, così si può togliere.
  const [ecomatico, setEcomatico] = useState<FiltroEcomatico>(
    params.get('ecomatico') === 'da_registrare' ? 'da_registrare' : 'tutti',
  );
  const [offset, setOffset] = useState(0);

  const userRole = useAuthStore((s) => s.user?.role ?? '');
  const puoAgire = canUscite(userRole);

  const reload = useCallback(() => {
    void (async () => {
      setLoading(true);
      try {
        const f: UscitaFiltri = { limit: LIMIT, offset };
        if (dataDa) f.data_da = dataDa;
        if (dataA) f.data_a = dataA;
        if (cliente) f.customer_id = cliente.id;
        if (rifiutoId) f.cer_rifiuto_id = rifiutoId;
        if (direzione) f.tipo_operazione = direzione;
        if (stato) f.stato = stato;
        if (ecomatico !== 'tutti') f.ecomatico = ecomatico;
        setRes(await listUscite(f));
      } finally {
        setLoading(false);
      }
    })();
  }, [dataDa, dataA, cliente, rifiutoId, direzione, stato, ecomatico, offset]);
  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    void (async () => { setRifiuti(await getCerRifiutiProdotti(true)); })();
  }, []);

  /**
   * Applica un filtro e torna alla prima pagina: restare all'offset 40 di una
   * lista che ora ne ha 3 mostrerebbe un vuoto che sembra «nessun risultato».
   *
   * Qui e non in un useEffect: azzerare state dentro un effect fa un render a
   * cascata (react-hooks/set-state-in-effect), e la pagina cambia perche' lo
   * ha chiesto l'utente, non perche' lo stato si e' sincronizzato da solo.
   */
  function filtra(applica: () => void) {
    applica();
    setOffset(0);
  }

  async function toggleEcomatico(it: UscitaListItem) {
    setBusy(true);
    try { await setEcomaticoUscita(it.id, !it.registrato_ecomatico); reload(); }
    finally { setBusy(false); }
  }

  // Etichette già tradotte nella struttura, non `t(o.label)`: check-i18n
  // verifica le chiavi letterali e rifiuta un `t(variabile)` fuori allowlist.
  const filtriEcomatico: { v: FiltroEcomatico; label: string }[] = [
    { v: 'tutti', label: t('magazzino.ecomatico.filtro_tutti') },
    { v: 'da_registrare', label: t('magazzino.ecomatico.filtro_da_registrare') },
    { v: 'registrati', label: t('magazzino.ecomatico.filtro_registrati') },
  ];

  const tutte = res?.items ?? [];
  // Il filtro esplicito su uno stato vince sull'interruttore: chi chiede
  // «annullata» le vuole vedere, altrimenti la lista tornerebbe vuota.
  const items = mostraAnnullate || stato === 'annullata'
    ? tutte
    : tutte.filter((i) => i.stato !== 'annullata');

  const totale = res?.total ?? 0;
  const da = totale === 0 ? 0 : offset + 1;
  const a = Math.min(offset + LIMIT, totale);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Truck size={20} className="text-sage" />
          <div>
            <h1 className="text-xl font-semibold text-ink-dark">{t('uscite.titolo')}</h1>
            <p className="text-xs text-ink-soft">{t('uscite.sottotitolo')}</p>
          </div>
        </div>
        {puoAgire && (
          <button type="button" onClick={() => navigate('/uscite/nuova')} data-testid="btn-nuova-uscita"
            className="px-3 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-sage-dark flex items-center gap-1.5">
            <Plus size={15} /> {t('uscite.nuova')}
          </button>
        )}
      </div>

      {/* Filtri */}
      <div className="flex flex-wrap items-end gap-2 mb-4">
        <div>
          <label className="block text-xs text-ink-soft mb-1">{t('uscite.filtri.data_da')}</label>
          <input type="date" className={inputCls} value={dataDa} onChange={(e) => filtra(() => setDataDa(e.target.value))} />
        </div>
        <div>
          <label className="block text-xs text-ink-soft mb-1">{t('uscite.filtri.data_a')}</label>
          <input type="date" className={inputCls} value={dataA} onChange={(e) => filtra(() => setDataA(e.target.value))} />
        </div>
        <div className="w-56">
          <ClienteSearch value={cliente} onChange={(c) => filtra(() => setCliente(c))} label={t('uscite.filtri.cliente')}
            placeholder={t('uscite.filtri.cliente_placeholder')} testid="filtro-cliente" />
        </div>
        <div>
          <label className="block text-xs text-ink-soft mb-1">{t('uscite.filtri.rifiuto')}</label>
          <select className={inputCls} value={rifiutoId} onChange={(e) => filtra(() => setRifiutoId(e.target.value))}>
            <option value="">{t('uscite.filtri.tutti_rifiuti')}</option>
            {rifiuti.map((r) => (
              <option key={r.id} value={r.id}>
                {r.nome_descrittivo ? `${r.nome_descrittivo} — ${r.cer}` : r.cer}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-ink-soft mb-1">{t('uscite.direzione.etichetta')}</label>
          <select className={inputCls} value={direzione} onChange={(e) => filtra(() => setDirezione(e.target.value as '' | TipoOperazione))}>
            <option value="">{t('uscite.direzione.tutte')}</option>
            <option value="vendita">{t('uscite.direzione.vendita')}</option>
            <option value="conferimento_a_terzi">{t('uscite.direzione.conferimento_a_terzi')}</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-ink-soft mb-1">{t('uscite.stato.etichetta')}</label>
          <select className={inputCls} value={stato} onChange={(e) => filtra(() => setStato(e.target.value as '' | StatoUscita))}>
            <option value="">{t('uscite.stato.tutti')}</option>
            <option value="registrata">{t('uscite.stato.registrata')}</option>
            <option value="fatturata">{t('uscite.stato.fatturata')}</option>
            <option value="pagata">{t('uscite.stato.pagata')}</option>
            <option value="annullata">{t('uscite.stato.annullata')}</option>
          </select>
        </div>
        <label className="flex items-center gap-1.5 text-sm text-ink-mid pb-1.5">
          <input type="checkbox" checked={mostraAnnullate} data-testid="toggle-annullate"
            onChange={(e) => setMostraAnnullate(e.target.checked)} />
          {t('uscite.stato.mostra_annullate')}
        </label>
        <div className="inline-flex rounded-lg border border-stone-border overflow-hidden mb-0.5">
          {filtriEcomatico.map((o) => (
            <button key={o.v} type="button" onClick={() => filtra(() => setEcomatico(o.v))}
              data-testid={`filtro-ecomatico-${o.v}`}
              className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                ecomatico === o.v ? 'bg-sage text-white' : 'bg-white text-ink-mid hover:bg-stone-bg'}`}>
              {o.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-stone-mid rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-bg">
            <tr className="text-left text-xs font-semibold text-ink-soft uppercase tracking-wide">
              <th className="px-3 py-3">{t('uscite.lista.col_codice')}</th>
              <th className="px-3 py-3">{t('uscite.lista.col_data')}</th>
              <th className="px-3 py-3">{t('uscite.lista.col_cliente')}</th>
              <th className="px-3 py-3">{t('uscite.lista.col_rifiuto')}</th>
              <th className="px-3 py-3 text-right">{t('uscite.lista.col_kg')}</th>
              <th className="px-3 py-3">{t('uscite.lista.col_fir')}</th>
              <th className="px-3 py-3">{t('uscite.lista.col_stato')}</th>
              <th className="px-3 py-3">{t('uscite.lista.col_ecomatico')}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-ink-soft">{t('uscite.caricamento')}</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-ink-soft">{t('uscite.vuoto')}</td></tr>
            ) : items.map((it) => (
              <tr key={it.id} data-testid={`uscita-${it.codice}`}
                className="border-t border-stone-mid/60 hover:bg-stone-bg/50 cursor-pointer"
                onClick={() => navigate(`/uscite/${it.id}`)}>
                <td className="px-3 py-2.5 font-mono text-xs font-medium">{it.codice}</td>
                <td className="px-3 py-2.5">{fmtData(it.data_partenza)}</td>
                <td className="px-3 py-2.5">{it.customer_ragione_sociale ?? '—'}</td>
                <td className="px-3 py-2.5">
                  <span className="text-ink-dark">{it.descrizione_rifiuto}</span>
                  <span className="ml-2 text-xs text-ink-faint">{it.cer_code}</span>
                  {/* V74: il codice non dice COSA c'e' sul camion. Un'uscita
                      puo' portare piu' definizioni dello stesso CER, e si
                      elencano tutte — i sacchi senza tipologia non aggiungono
                      una voce vuota, quindi la riga resta corta. */}
                  {it.tipologie.length > 0 && (
                    <span className="block text-xs text-ink-soft"
                      data-testid={`uscita-tipologie-${it.codice}`}>
                      {it.tipologie.join(' · ')}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-right">{fmtKg(it.peso_kg)}</td>
                <td className="px-3 py-2.5 text-ink-mid">{it.numero_fir}</td>
                <td className="px-3 py-2.5">
                  <StatoUscitaBadge stato={it.stato} />
                  {/* stopPropagation: la riga apre l'uscita, questo link no.
                      Lo stato dice gia' FATTURATA, il link dice DOVE. */}
                  {it.fattura_id && (
                    <button type="button" data-testid={`uscita-fattura-${it.id}`}
                      onClick={(e) => { e.stopPropagation(); navigate(`/fatturazione?fattura=${it.fattura_id}`); }}
                      className="block text-[11px] text-sage-dark hover:underline mt-0.5">
                      {t('uscite.lista.vedi_fattura')}
                    </button>
                  )}
                  {it.fattura_passiva_id && (
                    <button type="button" data-testid={`uscita-fattura-passiva-${it.id}`}
                      onClick={(e) => { e.stopPropagation(); navigate(`/fatturazione?sezione=passive&passiva=${it.fattura_passiva_id}`); }}
                      className="block text-[11px] text-sage-dark hover:underline mt-0.5">
                      {t('uscite.lista.vedi_fattura_passiva')}
                    </button>
                  )}
                </td>
                <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-2">
                    <EcomaticoBadge registrato={it.registrato_ecomatico} at={it.registrato_ecomatico_at}
                      daNome={it.registrato_ecomatico_da_nome} />
                    {puoAgire && it.stato !== 'annullata' && (
                      <button type="button" disabled={busy} onClick={() => toggleEcomatico(it)}
                        data-testid={`eco-toggle-${it.codice}`}
                        className="text-xs text-ink-soft hover:text-sage-dark disabled:opacity-50">
                        {it.registrato_ecomatico
                          ? t('uscite.lista.segna_da_registrare')
                          : t('uscite.lista.segna_registrato')}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {totale > LIMIT && (
          <div className="px-5 py-3 border-t border-stone-mid flex items-center justify-between">
            <p className="text-xs text-ink-soft">{t('uscite.lista.risultati', { da, a, totale })}</p>
            <div className="flex gap-2">
              <button type="button" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - LIMIT))}
                className="px-2.5 py-1 text-xs border border-stone-border rounded disabled:opacity-40 hover:bg-stone-bg">
                {t('uscite.lista.precedente')}
              </button>
              <button type="button" disabled={a >= totale} onClick={() => setOffset(offset + LIMIT)}
                className="px-2.5 py-1 text-xs border border-stone-border rounded disabled:opacity-40 hover:bg-stone-bg">
                {t('uscite.lista.successiva')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
